import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

export default function VerifyEmail() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const started = useRef(false);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Verifying your email...");

  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;

    if (!token) {
      setStatus("error");
      setMessage("Verification link is missing a token.");
      return undefined;
    }

    let timer;
    const verify = async () => {
      try {
        const { data } = await api.post("/auth/email/verify", { token });
        login(data.token, data.user);
        setStatus("success");
        setMessage("Email verified. Opening your account...");
        timer = window.setTimeout(() => nav("/dashboard", { replace: true }), 900);
      } catch (e) {
        setStatus("error");
        setMessage(formatApiError(e));
      }
    };

    verify();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [token, login, nav]);

  const isSuccess = status === "success";
  const isError = status === "error";

  return (
    <div className="min-h-screen bg-[#030705] text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-16 pb-24">
        <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#07110B] p-6 shadow-2xl shadow-[#D4AF37]/10">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-5">
            {isSuccess ? (
              <CheckCircle2 className="w-7 h-7 text-emerald-300" />
            ) : isError ? (
              <AlertTriangle className="w-7 h-7 text-red-300" />
            ) : (
              <Loader2 className="w-7 h-7 text-[#F1C40F] animate-spin" />
            )}
          </div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#F1C40F] mb-2">Email verification</p>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-3">
            {isSuccess ? "Verified" : isError ? "Link problem" : "Checking link"}
          </h1>
          <p className="text-white/70 leading-relaxed">{message}</p>
          {isError && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link
                to="/login"
                className="py-3 rounded-full border border-white/10 text-center font-bold hover:bg-white/5"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="py-3 rounded-full bg-[#D4AF37] text-black text-center font-bold hover:bg-[#F1C40F]"
              >
                Create account
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
